import { useCallback, useEffect, useState } from 'react'
import { Modal, colors } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { ConfigVersionDiffView } from './ConfigVersionDiffView'
import { buildSiteConfigVersionEntries, type SiteConfigHistoryRow } from './site-config-version-entries'

interface SiteConfigViewerModalProps {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly siteId: string
  readonly siteName: string
}

export function SiteConfigViewerModal({ isOpen, onClose, siteId, siteName }: SiteConfigViewerModalProps) {
  const [rows, setRows] = useState<readonly SiteConfigHistoryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [compareA, setCompareA] = useState<number | null>(null)
  const [compareB, setCompareB] = useState<number | null>(null)

  const fetchHistory = useCallback(async () => {
    setLoading(true)
    setError(null)
    setCompareA(null)
    setCompareB(null)
    try {
      // Path must NOT include /api — api.get prepends it.
      const res = await api.get<readonly SiteConfigHistoryRow[]>(`/fleet/sites/${siteId}/site-config/history?limit=50`)
      setRows(res.data ?? [])
    } catch (err) {
      // A 404 "No site config stored" is an empty state, not an error.
      if (err instanceof ApiError && err.status === 404) setRows([])
      else setError(err instanceof ApiError ? err.message : 'Failed to load site config history')
    } finally {
      setLoading(false)
    }
  }, [siteId])

  useEffect(() => {
    if (!isOpen || !siteId) return
    void fetchHistory()
  }, [isOpen, siteId, fetchHistory])

  const versionEntries = buildSiteConfigVersionEntries(rows)
  const canCompare = versionEntries.length >= 2
  const defaultB = versionEntries[0]?.version ?? null
  const defaultA = versionEntries[1]?.version ?? null
  const selectedB = compareB ?? defaultB
  const selectedA = compareA ?? defaultA
  const sideFor = (version: number | null) => versionEntries.find((e) => e.version === version) ?? null
  const leftSide = sideFor(selectedA)
  const rightSide = sideFor(selectedB)
  const current = versionEntries[0] ?? null

  const sectionHeader: React.CSSProperties = {
    fontWeight: 600,
    color: colors.textHeading,
    fontSize: '0.875rem',
    marginBottom: '0.5rem',
    marginTop: '1.25rem',
  }
  const selectStyle: React.CSSProperties = {
    background: colors.bgCard,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    padding: '0.25rem 0.5rem',
    fontSize: '0.8125rem',
  }
  const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.4rem 0',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: '0.8125rem',
  }
  const optionLabel = (e: (typeof versionEntries)[number]) =>
    `v${e.version}${e.meta?.changedAt ? ` — ${new Date(e.meta.changedAt).toLocaleDateString()}` : ''}`

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Site config: ${siteName}`} width="720px">
      {loading ? (
        <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading site config...</div>
      ) : error ? (
        <div style={{ color: colors.danger, padding: '1rem 0' }}>{error}</div>
      ) : versionEntries.length === 0 ? (
        <div style={{ color: colors.textMuted, padding: '1rem 0' }}>No site config stored for this site yet.</div>
      ) : (
        <>
          {current && (
            <>
              <div style={sectionHeader}>Current Config</div>
              <div
                style={{
                  display: 'flex',
                  gap: '1.5rem',
                  flexWrap: 'wrap',
                  fontSize: '0.8125rem',
                  color: colors.textMuted,
                }}
              >
                <span>
                  Version: <span style={{ color: colors.text, fontWeight: 500 }}>v{current.version}</span>
                </span>
                {current.meta?.pushedFrom && (
                  <span>
                    Updated by: <span style={{ color: colors.text, fontWeight: 500 }}>{current.meta.pushedFrom}</span>
                  </span>
                )}
                {current.meta?.changedAt && (
                  <span>
                    Updated:{' '}
                    <span style={{ color: colors.text, fontWeight: 500 }}>
                      {new Date(current.meta.changedAt).toLocaleString()}
                    </span>
                  </span>
                )}
              </div>
            </>
          )}

          <div style={sectionHeader}>Version History ({versionEntries.length})</div>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {versionEntries.map((e) => (
              <div key={e.version} style={rowStyle}>
                <strong style={{ color: colors.text }}>v{e.version}</strong>
                <span style={{ color: colors.textMuted }}>
                  {e.meta?.changedAt ? new Date(e.meta.changedAt).toLocaleString() : '—'}
                  {e.meta?.pushedFrom ? ` · by ${e.meta.pushedFrom}` : ''}
                </span>
              </div>
            ))}
          </div>

          {canCompare && (
            <>
              <div style={sectionHeader}>Compare Versions</div>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <label htmlFor="site-compare-a" style={{ fontSize: '0.8125rem', color: colors.textMuted }}>
                  Compare
                </label>
                <select
                  id="site-compare-a"
                  value={selectedA ?? ''}
                  onChange={(ev) => setCompareA(Number(ev.target.value))}
                  style={selectStyle}
                >
                  {versionEntries.map((e) => (
                    <option key={e.version} value={e.version}>
                      {optionLabel(e)}
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: '0.8125rem', color: colors.textMuted }}>with</span>
                <select
                  id="site-compare-b"
                  aria-label="Compare with"
                  value={selectedB ?? ''}
                  onChange={(ev) => setCompareB(Number(ev.target.value))}
                  style={selectStyle}
                >
                  {versionEntries.map((e) => (
                    <option key={e.version} value={e.version}>
                      {optionLabel(e)}
                    </option>
                  ))}
                </select>
              </div>
              {leftSide && rightSide && <ConfigVersionDiffView left={leftSide} right={rightSide} />}
            </>
          )}
        </>
      )}
    </Modal>
  )
}
